// CustomToggleButton.jsx
import { useState, useCallback, memo } from "react";

const CustomToggleButton = memo(
  ({
    value = false,
    onChange,
    label = "",
    disabled = false,
    size = "sm", // "sm" | "md" | "lg"
    color = "default", // "default" | "success" | "warning" | "danger"
  }) => {
    const [internalValue, setInternalValue] = useState(value);

    const isControlled = onChange !== undefined;
    const isOn = isControlled ? value : internalValue;

    const handleToggle = useCallback(() => {
      if (disabled) return;
      if (isControlled) {
        onChange(!value);
      } else {
        setInternalValue((prev) => !prev);
      }
    }, [disabled, isControlled, onChange, value]);

    const colorMap = {
      default: { on: "#1976d2", track: "#90caf9" },
      success: { on: "#2e7d32", track: "#a5d6a7" },
      warning: { on: "#e65100", track: "#ffcc80" },
      danger: { on: "#c62828", track: "#ef9a9a" },
    };

    const sizeMap = {
      sm: { track: { width: 32, height: 18 }, thumb: 12, translateX: 14 },
      md: { track: { width: 44, height: 24 }, thumb: 16, translateX: 20 },
      lg: { track: { width: 56, height: 30 }, thumb: 20, translateX: 26 },
    };

    const { on, track: trackColor } = colorMap[color] || colorMap.default;
    const { track, thumb, translateX } = sizeMap[size] || sizeMap.md;

    const styles = {
      wrapper: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        userSelect: "none",
      },
      track: {
        position: "relative",
        width: track.width,
        height: track.height,
        borderRadius: track.height / 2,
        backgroundColor: isOn ? on : "#bdbdbd",
        transition: "background-color 0.25s ease",
        flexShrink: 0,
      },
      thumb: {
        position: "absolute",
        top: (track.height - thumb) / 2,
        left: (track.height - thumb) / 2,
        width: thumb,
        height: thumb,
        borderRadius: "50%",
        backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
        transform: isOn ? `translateX(${translateX}px)` : "translateX(0px)",
        transition: "transform 0.25s ease",
      },
      label: {
        fontSize: size === "sm" ? 12 : size === "lg" ? 16 : 14,
        color: "#333",
        lineHeight: 1,
      },
    };

    return (
      <div
        style={styles.wrapper}
        onClick={handleToggle}
        role="switch"
        aria-checked={isOn}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => (e.key === " " || e.key === "Enter") && handleToggle()}
      >
        <div style={styles.track}>
          <div style={styles.thumb} />
        </div>
        {label && <span style={styles.label}>{label}</span>}
      </div>
    );
  }
);

export default CustomToggleButton;
